import React from "react";
import "./privacy-policy.css";
import Navbar from "./Navbar";

const PrivacyPolicy = () => {
  return (
    <>
      <Navbar />

      <main className="policy">
        <div className="policy__inner">
          <header className="policy__header">
            <h1 className="policy__title">Privacy Policy</h1>
            <p className="policy__updated">
              Last Updated: <strong>1st of September 2025</strong>
            </p>
          </header>

          <p className="policy__lead">
            At <span className="accent">FlxDeck</span>, your privacy is our priority. This Privacy Policy explains how we collect, use, disclose, and protect your personal information in compliance
            with Quebec’s <em>Loi sur la protection des renseignements personnels dans le secteur privé</em> and Canada’s <em>PIPEDA</em> (Personal Information Protection and Electronic Documents
            Act). By using our services, you agree to the practices described in this policy.
          </p>

          <section className="policy__section" id="info-we-collect">
            <h2>1. Information We Collect</h2>
            <p>We collect and process personal information to provide, improve, and secure our services.</p>

            <h3>1.1 Personal Information</h3>
            <p>We may collect:</p>
            <ul>
              <li>Name, email address, phone number, and home/business address.</li>
              <li>Payment details when you purchase our services.</li>
              <li>Information provided voluntarily during inquiries, consultations, or service delivery.</li>
            </ul>

            <h3>1.2 Sensitive Information</h3>
            <p>Sensitive data (e.g., passwords, security details) is only collected with your explicit consent for specific services, such as penetration testing services.</p>

            <h3>1.3 Log Files</h3>
            <p>
              https://www.flxdeck.com follows a standard procedure of using log files. These files log visitors when they visit websites. The information collected by log files includes internet
              protocol (IP) addresses, browser type, Internet Service Provider (ISP), date and time stamp, referring/exit pages, and possibly the number of clicks. These are not linked to any
              personally identifiable information. The purpose of the information is for analyzing trends, administering the site, tracking users' movement on the website, and gathering demographic
              information.
            </p>
          </section>

          <section className="policy__section">
            <h2>2. How We Use Your Information</h2>
            <ul>
              <li>To provide and personalize our services, including monitoring and security solutions.</li>
              <li>To communicate with you about updates, promotions, and service improvements.</li>
              <li>To comply with legal obligations and ensure regulatory compliance.</li>
              <li>To analyze and improve the performance of our website and services.</li>
              <li>To bill our services and handle collections.</li>
            </ul>
          </section>

          <section className="policy__section">
            <h2>3. Sharing Your Information</h2>
            <p>Your information is handled responsibly and shared only when necessary:</p>
            <ul>
              <li>
                <strong>Service Providers:</strong> Trusted third parties who assist in delivering our services (e.g., payment processors).
              </li>
              <li>
                <strong>Legal Obligations:</strong> When required by law, such as court orders or government regulations.
              </li>
              <li>
                <strong>Marketing Partners:</strong> Only with your explicit consent for promotional purposes.
              </li>
            </ul>
            <p>We ensure all third parties adhere to strict confidentiality and data protection standards.</p>
          </section>

          <section className="policy__section">
            <h2>4. Your Rights</h2>
            <p>You have the following rights regarding your personal information:</p>
            <ul>
              <li>
                <strong>Access:</strong> Request a copy of your personal information.
              </li>
              <li>
                <strong>Correction:</strong> Update any inaccuracies in your data.
              </li>
              <li>
                <strong>Deletion:</strong> Request the removal of your data where applicable.
              </li>
              <li>
                <strong>Consent Management:</strong> Withdraw consent for data processing at any time.
              </li>
              <li>
                <strong>Data Portability:</strong> Request the transfer of your data in a structured, machine-readable format.
              </li>
            </ul>

            <div className="policy__card">
              <p className="policy__card-title">To exercise your rights, contact our Privacy Officer:</p>
              <p>
                <strong>Christian El-Hamaoui</strong>
                <br />
                Privacy Officer
                <br />
                <a href="mailto:contact@flxdeck.com">contact@flxdeck.com</a>
              </p>
            </div>
          </section>

          <section className="policy__section">
            <h2>5. Data Security</h2>
            <p>
              We implement a comprehensive range of security measures to ensure the protection and accuracy of the personal information we maintain against unauthorized use or modification. These
              measures include:
            </p>
            <ul>
              <li>Designating a Privacy Officer</li>
              <li>Implementing administrative controls</li>
              <li>Establishing physical security measures</li>
              <li>Raising awareness and providing training for staff</li>
              <li>Using technological security devices</li>
            </ul>
            <p>
              Despite our precautions when collecting your personal data, it is important to note that data transmission over the Internet, including by email, is never completely secure. We make
              every effort to protect personal information, but we cannot guarantee the security of data that we receive or transmit.
            </p>
          </section>

          <section className="policy__section">
            <h2>6. Data Retention</h2>
            <p>We retain your personal information only as long as necessary to:</p>
            <ul>
              <li>Fulfill the purposes for which it was collected.</li>
              <li>Comply with legal, tax, or regulatory requirements.</li>
              <li>Resolve disputes and enforce agreements.</li>
            </ul>
            <p>Once retention is no longer required, your data is securely deleted or anonymized.</p>
          </section>

          <section className="policy__section">
            <h2>7. Cookies and Tracking</h2>
            <p>
              We do not collect or utilize cookies on its website. We prioritize your privacy and do not track your browsing activities for advertising or marketing purposes. We are committed to
              providing a secure and private browsing experience for our users. Please note that while we do not use cookies, certain third-party services or applications integrated into our website
              may utilize cookies, and their use is subject to the respective third parties' privacy policies.
            </p>
          </section>

          <section className="policy__section">
            <h2>8. Cross-Border Data Transfers</h2>
            <p>
              Your data may be stored or processed outside Canada (e.g., the United States or Europe) by our service providers. In such cases, we ensure compliance with applicable privacy laws and
              adequate data protection measures.
            </p>
          </section>

          <section className="policy__section">
            <h2>9. Updates to This Policy</h2>
            <p>
              We may update this Privacy Policy periodically to reflect changes in our practices or legal obligations. Significant changes will be communicated via email or a notice on our website.
            </p>
          </section>

          <section className="policy__section">
            <h2>10. Contact Us</h2>
            <p>
              For any questions, concerns, or requests related to this Privacy Policy, please contact our Privacy Officer at <a href="mailto:contact@flxdeck.com">contact@flxdeck.com</a>.
            </p>
          </section>
        </div>
      </main>
    </>
  );
};

export default PrivacyPolicy;
